import {
  Alert,
  Box,
  Button,
  Snackbar,
  TextField,
  Typography,
  styled
} from '@mui/material';
import CustomGlowButton from './GlowingButton';
import EmailIcon from "@mui/icons-material/Email";
import UnsubscribeModal from './UnsubscribeModel';
import { useState } from 'react';
import axios from 'axios';

const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: "50vh",
  background: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "70%",
    height: "60%",
    left: "85%",
    top: "90%",
    transform: "translate(-100%, -50%)",
    background: "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    backdropFilter: 'blur(300px)',
    zIndex: 0,
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
  }
}));

const GlassBox = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  padding: theme.spacing(4),
  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
  borderRadius: "20px",
  borderWidth: "2px",
  borderStyle: "solid",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",

  [theme.breakpoints.down("md")]: {
    width: "90%",
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(4),
  }
}));

const GradientTitle = styled(Typography)(({ theme }) => ({
  fontSize: "48px",
  fontWeight: 700,
  lineHeight: 1.2,
  background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: theme.spacing(3),
  textAlign: "center",

  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
  }
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderRadius: "12px",
    height: "56px",

    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.1)",
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#6366f1",
    },

    /* CHROME AUTOFILL FIX */
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 100px rgba(255,255,255,0.03) inset",
      WebkitTextFillColor: "#fff",
      caretColor: "#fff",
      borderRadius: "12px",
      transition: "background-color 5000s ease-in-out 0s",
    },

    "& input:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 100px rgba(255,255,255,0.03) inset",
      WebkitTextFillColor: "#fff",
    },

    "& input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 100px rgba(255,255,255,0.03) inset",
      WebkitTextFillColor: "#fff",
    },
  },

  "& .MuiInputBase-input": {
    color: "#fff",

    "&::placeholder": {
      color: "#8C8CC2",
      opacity: 1,
    },
  },
}));

const StayInLoop = () => {
  const [modelOpen, setModelOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [snackbar, setSnackbar] =
    useState(false);


  const handleSubmit = async () => {
    try {
      if (!email) {
        alert(
          "Please enter email"
        );
        return;
      }
      await axios.post("https://dev.ienergydigital.com/task-api/api/email-suits/subscribe",
        { email },
      );
      setSnackbar(true);
      setEmail("");
    } catch (error) {
      console.log(error);
    } 
  };

  return (
    <MainContainer>
      <ContentWrapper>
        <GlassBox>
          <GradientTitle
            variant="h2"
            sx={{
              color: '#3A3AF4',
              mb: 2,
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' }
            }}
          >
            Insights That Keep You Moving Forward
          </GradientTitle>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              fontSize: { xs: '0.875rem', md: '18px', lg: '18px' },
              color: '#8C8CC2',
              textAlign: 'center'
            }}
          >
            Stay informed with industry insights, product updates, operational strategies, and enterprise trends shaping modern industrial organizations.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              mb: 2
            }}
          >
            <CustomTextField
              fullWidth
              placeholder="example@gmail.com"
              name ="email"
              value={email}
              onChange={(e) =>setEmail(e.target.value)}
              variant="outlined"
              sx={{ maxWidth: { md: 650 } }}
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: "#6366f1", mr: 1 }} />
              }}
            />
            <CustomGlowButton
              text="Subscribe"
              onClick={handleSubmit}
            />
          </Box>

          <Button
            onClick={() => setModelOpen(true)}
            variant="text"
            sx={{
              color: '#8C8CC2',
              textDecoration: 'underline',
              '&:hover': {
                color: '#8C8CC2',
              }
            }}
          >
            Unsubscribe
          </Button>
        </GlassBox>
      </ContentWrapper>
      <UnsubscribeModal open={modelOpen} onClose={() => setModelOpen(false)} />
      <Snackbar
        open={snackbar}
        autoHideDuration={7000}
        onClose={() =>
          setSnackbar(false)
        }
        anchorOrigin={{
          vertical: "top",
          horizontal:
            "center",
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() =>
            setSnackbar(false)
          }
          sx={{
            width: "100%",
            borderRadius:
              "12px",
            alignItems:
              "center",
          }}
        >
          You have been
          successfully
          subscribed from
          future newsletters.
          You receive
          an emails.
        </Alert>
      </Snackbar>

    </MainContainer>
  );
};

export default StayInLoop;